// RTT vs send rate (owned by the web:charts agent).
//
// Latency under load: p50 (solid) and p99 (dashed) round-trip time against
// packets-per-second, one colour per node. A p99 that fans out from p50 as pps
// climbs is the fingerprint of a filling buffer (bufferbloat) or an overloaded
// path. Values are averaged across sizes and repeats at each rate.

import { useMemo } from 'react';
import uPlot from 'uplot';
import type { NodeInfo, SweepResult } from '@/engine/types';
import { UplotChart } from './Uplot';
import { EmptyNote } from './Canvas';
import { categoricalColors, useChartTheme } from './palette';

export interface RttVsPpsProps {
  sweep: SweepResult;
  nodes?: NodeInfo[];
  height?: number;
}

interface Prepared {
  data: uPlot.AlignedData;
  nodeIds: string[];
  pps: number[];
}

function prepare(sweep: SweepResult): Prepared {
  const pps = [...new Set(sweep.cells.map((c) => c.pps))].sort((a, b) => a - b);
  const nodeIds = [...new Set(sweep.cells.map((c) => c.nodeId))];
  // node -> pps -> {p50sum, p99sum, n}
  const acc = new Map<string, Map<number, { p50: number; p99: number; n: number }>>();
  for (const cell of sweep.cells) {
    let row = acc.get(cell.nodeId);
    if (!row) {
      row = new Map();
      acc.set(cell.nodeId, row);
    }
    const a = row.get(cell.pps) ?? { p50: 0, p99: 0, n: 0 };
    a.p50 += cell.run.metrics.p50;
    a.p99 += cell.run.metrics.p99;
    a.n += 1;
    row.set(cell.pps, a);
  }
  const series: (number | null)[][] = [];
  for (const id of nodeIds) {
    const row = acc.get(id);
    const p50Line: (number | null)[] = [];
    const p99Line: (number | null)[] = [];
    for (const rate of pps) {
      const a = row?.get(rate);
      p50Line.push(a && a.n ? a.p50 / a.n : null);
      p99Line.push(a && a.n ? a.p99 / a.n : null);
    }
    series.push(p50Line, p99Line);
  }
  return { data: [pps, ...series] as uPlot.AlignedData, nodeIds, pps };
}

export function RttVsPps({ sweep, nodes, height = 240 }: RttVsPpsProps) {
  const { palette } = useChartTheme();
  const prep = useMemo(() => prepare(sweep), [sweep]);
  const colors = categoricalColors(palette);
  const nameOf = (id: string) => nodes?.find((n) => n.id === id)?.name ?? id;

  const options = useMemo<Omit<uPlot.Options, 'width'>>(() => {
    const series: uPlot.Series[] = [{ label: 'pps' }];
    prep.nodeIds.forEach((id, i) => {
      const color = colors[i % colors.length];
      series.push({
        label: `${nameOf(id)} p50`,
        stroke: color,
        width: 1.75,
        points: { show: true, size: 5 },
        spanGaps: true,
      });
      series.push({
        label: `${nameOf(id)} p99`,
        stroke: color,
        width: 1.5,
        dash: [5, 4],
        points: { show: true, size: 4 },
        spanGaps: true,
      });
    });
    return {
      height,
      legend: { show: true },
      cursor: { focus: { prox: 24 } },
      scales: { x: { time: false }, y: { auto: true } },
      axes: [
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'Send rate (pps)',
          labelFont: '12px system-ui, sans-serif',
        },
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'RTT (ms)',
          labelFont: '12px system-ui, sans-serif',
        },
      ],
      series,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette, height, prep, nodes]);

  if (prep.pps.length === 0) return <EmptyNote label="No rate sweep to chart." />;

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <UplotChart options={options} data={prep.data} ariaLabel="RTT percentiles versus send rate" />
      </div>
      <figcaption style={{ fontSize: 12, color: palette.textMuted, marginTop: 4 }}>
        Solid = p50, dashed = p99. A widening gap as rate rises indicates buffering under load.
      </figcaption>
    </figure>
  );
}
